import { useCallback, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import type { CategoryRow, EventTypeRow } from '@/db/types'
import {
  archiveEventType,
  CatalogGuardError,
  createCategory,
  createEventType,
  deleteEventType,
  listCategories,
  listEventTypes,
  moveCategory,
  moveEventType,
  setCategoryCollapsed,
  setPinned,
} from '@/core/repository/catalogRepository'
import { logger } from '@/core/logging/logger'
import { TypeRow } from './TypeRow'
import { TypeEditor } from './TypeEditor'
import { CategoryEditor } from './CategoryEditor'
import './manage.css'

interface ManageScreenProps {
  onClose: () => void
  onChanged: () => void
}

/** The bucket for types with no category. Not a stored row; a rendering group. */
const UNCATEGORISED = '__none__'

/**
 * Managing the catalogue: categories and the event types inside them.
 *
 * Ordered exactly as the picker orders them, so what you arrange here is what
 * you see when marking. That correspondence is the whole point of the screen
 * and is why archived rows are hidden by default -- an arrangement that
 * includes things the picker will not show is not a preview of anything.
 */
export function ManageScreen({ onClose, onChanged }: ManageScreenProps) {
  const [showArchived, setShowArchived] = useState(false)
  const [editingType, setEditingType] = useState<EventTypeRow | null>(null)
  const [editingCategory, setEditingCategory] = useState<CategoryRow | null>(null)
  const [addingTo, setAddingTo] = useState<string | null>(null)
  const [draftName, setDraftName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const categories = useLiveQuery(() => listCategories(showArchived), [showArchived], [])
  const types = useLiveQuery(() => listEventTypes(showArchived), [showArchived], [])

  const typesByCategory = useMemo(() => {
    const map = new Map<string, EventTypeRow[]>()
    for (const type of types) {
      const key = type.categoryId ?? UNCATEGORISED
      const list = map.get(key)
      if (list) list.push(type)
      else map.set(key, [type])
    }
    return map
  }, [types])

  const uncategorised = typesByCategory.get(UNCATEGORISED) ?? []

  const run = useCallback(
    (work: () => Promise<unknown>) => {
      setError(null)
      void work()
        .then(() => onChanged())
        .catch((err: unknown) => {
          if (err instanceof CatalogGuardError) {
            setError(`${err.message} Archive it, or merge it into another type from its editor.`)
            return
          }
          logger.error('db', 'Catalogue change failed', err)
          setError('That did not work.')
        })
    },
    [onChanged],
  )

  const submitDraft = useCallback(() => {
    const name = draftName.trim()
    if (!name) return

    if (addingTo === null) return

    if (addingTo === '__category__') {
      run(() => createCategory(name))
    } else {
      run(() => createEventType({ name, categoryId: addingTo === UNCATEGORISED ? null : addingTo }))
    }

    setDraftName('')
    setAddingTo(null)
  }, [draftName, addingTo, run])

  const renderGroup = (
    key: string,
    heading: React.ReactNode,
    groupTypes: EventTypeRow[],
    collapsed: boolean,
  ) => (
    <section className="mg-group" key={key}>
      {heading}

      {collapsed ? null : (
        <>
          {groupTypes.map((type, index) => (
            <TypeRow
              key={type.id}
              type={type}
              canMoveUp={index > 0}
              canMoveDown={index < groupTypes.length - 1}
              onEdit={setEditingType}
              onMove={(id, direction) => run(() => moveEventType(id, direction))}
              onTogglePin={(t) => run(() => setPinned(t.id, t.isPinned === 0))}
              onArchive={(t) => run(() => archiveEventType(t.id, t.isArchived === 0))}
              onDelete={(t) => run(() => deleteEventType(t.id))}
            />
          ))}

          {addingTo === key ? (
            <DraftInput
              placeholder="New event type"
              value={draftName}
              onChange={setDraftName}
              onSubmit={submitDraft}
              onCancel={() => {
                setAddingTo(null)
                setDraftName('')
              }}
            />
          ) : (
            <button
              className="mg-add"
              onClick={() => {
                setDraftName('')
                setAddingTo(key)
              }}
            >
              + Event type
            </button>
          )}
        </>
      )}
    </section>
  )

  return (
    <div className="mg-sheet" role="dialog" aria-label="Event types and categories">
      <header className="mg-head">
        <button
          className="mg-head-btn"
          onClick={() => setShowArchived((v) => !v)}
          aria-pressed={showArchived}
        >
          {showArchived ? 'Hide archived' : 'Archived'}
        </button>
        <h2 className="mg-title">Event types</h2>
        <button className="mg-head-btn mg-head-btn--strong" onClick={onClose}>
          Done
        </button>
      </header>

      {error ? (
        <p className="mg-status mg-status--error" role="status">
          {error}
        </p>
      ) : null}

      <div className="mg-body scroll-y">
        <p className="mg-hint mg-hint--top">
          Tap a row to edit it. Swipe a row left for Archive and Delete.
        </p>

        {categories.map((category, index) =>
          renderGroup(
            category.id,
            <div className="mg-cat-head">
              <button
                className="mg-cat-toggle"
                onClick={() =>
                  run(() => setCategoryCollapsed(category.id, category.isCollapsed === 0))
                }
                aria-expanded={category.isCollapsed === 0}
                aria-label={`${category.isCollapsed === 1 ? 'Expand' : 'Collapse'} ${category.name}`}
              >
                <span className="mg-cat-dot" style={{ background: category.colorHex }} aria-hidden="true" />
                <span className="mg-cat-name">{category.name}</span>
                {category.isArchived === 1 ? <span className="mg-row-tag">Archived</span> : null}
                <span className="mg-cat-chevron" aria-hidden="true">
                  {category.isCollapsed === 1 ? '+' : '−'}
                </span>
              </button>

              <button
                className="mg-row-btn"
                onClick={() => setEditingCategory(category)}
                aria-label={`Edit category ${category.name}`}
              >
                <span aria-hidden="true">✎</span>
              </button>
              <button
                className="mg-row-btn"
                onClick={() => run(() => moveCategory(category.id, -1))}
                disabled={index === 0}
                aria-label={`Move category ${category.name} up`}
              >
                <span aria-hidden="true">↑</span>
              </button>
              <button
                className="mg-row-btn"
                onClick={() => run(() => moveCategory(category.id, 1))}
                disabled={index === categories.length - 1}
                aria-label={`Move category ${category.name} down`}
              >
                <span aria-hidden="true">↓</span>
              </button>
            </div>,
            typesByCategory.get(category.id) ?? [],
            category.isCollapsed === 1,
          ),
        )}

        {/* Only appears once something is actually uncategorised -- normally
            after a category is deleted. An always-present empty group would
            just be a confusing extra heading. */}
        {uncategorised.length > 0
          ? renderGroup(
              UNCATEGORISED,
              <div className="mg-cat-head">
                <span className="mg-cat-toggle mg-cat-toggle--static">
                  <span className="mg-cat-dot mg-cat-dot--none" aria-hidden="true" />
                  <span className="mg-cat-name">Uncategorised</span>
                </span>
              </div>,
              uncategorised,
              false,
            )
          : null}

        <section className="mg-group">
          {addingTo === '__category__' ? (
            <DraftInput
              placeholder="New category"
              value={draftName}
              onChange={setDraftName}
              onSubmit={submitDraft}
              onCancel={() => {
                setAddingTo(null)
                setDraftName('')
              }}
            />
          ) : (
            <button
              className="mg-add mg-add--strong"
              onClick={() => {
                setDraftName('')
                setAddingTo('__category__')
              }}
            >
              + Category
            </button>
          )}
        </section>
      </div>

      {editingType ? (
        <TypeEditor
          type={editingType}
          onClose={() => setEditingType(null)}
          onChanged={onChanged}
        />
      ) : null}

      {editingCategory ? (
        <CategoryEditor
          category={editingCategory}
          onClose={() => setEditingCategory(null)}
          onChanged={onChanged}
        />
      ) : null}
    </div>
  )
}

interface DraftInputProps {
  placeholder: string
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  onCancel: () => void
}

function DraftInput({ placeholder, value, onChange, onSubmit, onCancel }: DraftInputProps) {
  return (
    <div className="mg-draft">
      <input
        className="mg-input"
        autoFocus
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            onSubmit()
          }
          if (e.key === 'Escape') onCancel()
        }}
      />
      <button className="mg-btn mg-btn--inline" onClick={onSubmit} disabled={!value.trim()}>
        Add
      </button>
      <button className="mg-btn mg-btn--inline" onClick={onCancel}>
        Cancel
      </button>
    </div>
  )
}
